import * as React from "react";
import { Button, SpaceBetween, TextContent, Toggle } from "@amzn/awsui-components-react";
import ReactFlow, {
    Controls,
    MiniMap,
    Background,
    BackgroundVariant,
    Panel,
    FitViewOptions,
    ReactFlowInstance,
    Connection,
    OnSelectionChangeParams,
    NodeChange,
    EdgeChange,
    Node,
    Edge,
} from "reactflow";
import { useLayoutManager } from "../../hooks/decision-tree-node-hook";
import { SaveStatus, SaveType } from "../../hooks/use-save-status";
import {
    ConnectionAnswerPopover,
    PendingConnection
} from "./connection-popover/ConnectionAnswerPopover";
import { classifySubtree } from "../../utils/decisiontree/subtree-classifier";
import { CustomEdge } from "./custom-resources/CustomEdge";
import { ORPHAN_PURPLE, CROSS_ORPHAN_BLUE, CROSS_CONNECT_AMBER } from "./decision-tree-colors";

const FIT_VIEW_OPTIONS: FitViewOptions = { padding: 0.2 };

const DRAG_TIP_STYLE: React.CSSProperties = {
    background: "#0972d3",
    color: "white",
    padding: "12px 20px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "500",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    animation: "fadeIn 0.3s ease-in"
};

export interface ReactFlowCanvasProps {
    rfKey: number;
    layoutManager: ReturnType<typeof useLayoutManager>;
    nodeTypes: any;
    edgeTypes: any;
    onNodeDoubleClick: (event: any, node: any) => void;
    onNodeDragStart: (event: any, node: any) => void;
    onNodeDragStop: (event: any, node: any) => void;
    rootNodeId: string;
    showDragTip: boolean;
    saveStatus: SaveStatus;
    saveType: SaveType;
    closeEditor: () => void;
    handleLayoutToggle: (checked: boolean) => void;
    /** Whether the Decision Tree Enhancement lab is enabled. Controls enhanced UI features. */
    enhancementLabEnabled?: boolean;
    onCreateOrphanNode?: () => void;
    orphanNodeCount?: number;
    /** Context menu action: remove a single link (edge) between parent and child. Child becomes orphan. */
    onRemoveLink?: (sourceNodeId: string, targetNodeId: string, answerText: string) => void;
    /** Context menu action: delete a node entirely. */
    onDeleteNode?: (nodeId: string) => void;
    /** Context menu action: copy a single node as orphan. */
    onCopyNode?: (nodeId: string) => void;
    /** Context menu action: copy a node and its subtree as orphan subtree. */
    onCopySubtree?: (nodeId: string) => void;
    /** Drag-to-link: add a new link between two nodes with the given answer text. */
    onAddLink?: (sourceNodeId: string, targetNodeId: string, answerText: string) => void;
    /** Provides current content for cycle detection during drag-to-link. */
    getContentForValidation?: () => { allNodes: Array<{ id: string; children: Array<{ nodeId: string }> }> };
    /** Context menu action: edit the answer text on an edge. */
    onEditAnswer?: (sourceNodeId: string, targetNodeId: string, oldAnswer: string, newAnswer: string) => void;
    /** Unified bulk action: delete selected nodes and/or remove selected links in a single API call.
     *  Smart dedup is handled by the parent — redundant link removals are filtered out. */
    onDeleteSelected?: (params: {
        nodeIds: string[];
        edges: Array<{ sourceNodeId: string; targetNodeId: string; answerText: string }>;
    }) => void;
    /** Clipboard state from parent — non-null means something is copied and ready to paste. */
    clipboard?: { type: "node" | "subtree"; sourceNodeId: string } | null;
    /** Called when user wants to paste clipboard content at a canvas position (flow coordinates). */
    onPaste?: (position: { x: number; y: number }) => void;
    /** Called to clear the clipboard. */
    onClearClipboard?: () => void;
    /** Delete a node and all its descendants (subtree). */
    onDeleteSubtree?: (nodeId: string) => void;
    /** Relink: reconnect an existing edge to a different source or target node.
     *  Preserves the original answer text. Handles both target-change (single API call)
     *  and source-change (two sequential API calls: add-first, then remove-old). */
    onReconnectEdge?: (oldSourceId: string, oldTargetId: string, newSourceId: string, newTargetId: string, answerText: string) => void;
}

/** Tracked selection state for multi-select operations */
export interface SelectionState {
    /** IDs of currently selected nodes */
    selectedNodeIds: string[];
    /** IDs of currently selected edges (ReactFlow edge IDs) */
    selectedEdgeIds: string[];
    /** Number of selected nodes that are deletable (non-root) */
    deletableNodeCount: number;
}

/**
 * Inner ReactFlow component rendered inside <ReactFlowProvider>.
 *
 * Handles:
 * - ReactFlow rendering with nodes/edges
 * - Fit view on initial load and layout changes
 * - Toolbar (Go Back button, Layout toggle, Save status)
 * - MiniMap with color coding
 * - Helper panel
 */

/** Extracted selection toolbar — renders action buttons based on current selection. */
const SelectionToolbar: React.FC<{
    selection: SelectionState;
    rootNodeId: string;
    edges: any[];
    onNodeDoubleClick: (event: any, node: any) => void;
    onEditAnswer?: (sourceNodeId: string, targetNodeId: string, oldAnswer: string, newAnswer: string) => void;
    onCopyNode?: (nodeId: string) => void;
    onCopySubtree?: (nodeId: string) => void;
    onDeleteSelected: () => void;
    onDeleteSubtree?: (nodeId: string) => void;
    onClearSelection: () => void;
    setEditingAnswer: (val: { sourceNodeId: string; targetNodeId: string; currentAnswer: string; position: { x: number; y: number } } | null) => void;
    /** For subtree hover preview — sets className on nodes */
    setNodes?: (updater: (nodes: Node[]) => Node[]) => void;
    /** For subtree hover preview — provides content for BFS */
    getContentForValidation?: () => { allNodes: Array<{ id: string; children: Array<{ nodeId: string }>; deleted?: boolean }> };
}> = ({ selection, rootNodeId, edges, onNodeDoubleClick, onEditAnswer, onCopyNode, onCopySubtree, onDeleteSelected, onDeleteSubtree, onClearSelection, setEditingAnswer, setNodes, getContentForValidation }) => {
    const totalSelected = selection.selectedNodeIds.length + selection.selectedEdgeIds.length;
    const isMulti = totalSelected >= 2;
    const hasDeletable = selection.deletableNodeCount > 0 || selection.selectedEdgeIds.length > 0;
    const includesRoot = selection.selectedNodeIds.includes(rootNodeId);
    const singleNodeSelected = selection.selectedNodeIds.length === 1 && selection.selectedEdgeIds.length === 0;
    const singleEdgeSelected = selection.selectedEdgeIds.length === 1 && selection.selectedNodeIds.length === 0;
    const selectedNodeId = selection.selectedNodeIds[0];

    const selectedNodeHasChildren = singleNodeSelected ? edges.some(e => e.source === selectedNodeId) : false;
    const selectedNodeIsRoot = singleNodeSelected && selectedNodeId === rootNodeId;

    /**
     * Lazily classify subtree nodes for hover preview, with ref-based memoization.
     * The DFS in classifySubtree is O(nodes) — caching avoids re-running it on every
     * hover event when the tree hasn't changed. Cache invalidates when selectedNodeId
     * changes or when the content object identity changes (replaced on every mutation
     * by applyOutputAndReload).
     */
    const subtreeCache = React.useRef<{
        nodeId: string; content: unknown;
        result: { deletable: string[]; preserved: string[]; all: string[] };
    } | null>(null);
    const collectSubtreeClassified = React.useCallback(() => {
        if (!singleNodeSelected || !getContentForValidation)
            return { deletable: [] as string[], preserved: [] as string[], all: [] as string[] };
        const content = getContentForValidation();
        const cached = subtreeCache.current;
        if (cached && cached.nodeId === selectedNodeId && cached.content === content) return cached.result;
        const result = classifySubtree(selectedNodeId, content.allNodes);
        subtreeCache.current = { nodeId: selectedNodeId, content, result };
        return result;
    }, [selectedNodeId, singleNodeSelected, getContentForValidation]);

    /** Apply/remove preview className on subtree nodes */
    const showPreview = React.useCallback((mode: "copy" | "delete") => {
        if (!setNodes || !singleNodeSelected) return;
        const classified = collectSubtreeClassified();
        if (classified.all.length === 0) return;
        const allSet = new Set(classified.all);
        const delSet = new Set(classified.deletable);
        const presSet = new Set(classified.preserved);
        // Append preview class to existing className (preserves any other classes).
        // Guards against duplicate stacking from rapid hover in/out with React batching.
        const appendClass = (existing: string | undefined, cls: string) =>
            existing?.includes(cls) ? existing : [existing, cls].filter(Boolean).join(" ");
        if (mode === "copy") {
            setNodes(nds => nds.map(n => allSet.has(n.id)
                ? { ...n, className: appendClass(n.className, "subtree-preview-copy") }
                : n));
        } else {
            setNodes(nds => nds.map(n => {
                if (delSet.has(n.id)) return { ...n, className: appendClass(n.className, "subtree-preview-delete") };
                if (presSet.has(n.id)) return { ...n, className: appendClass(n.className, "subtree-preview-preserved") };
                return n;
            }));
        }
    }, [setNodes, singleNodeSelected, selectedNodeId, collectSubtreeClassified]);

    const clearPreview = React.useCallback(() => {
        if (!setNodes) return;
        // Strip only subtree-preview-* classes, preserving any other classes
        setNodes(nds => nds.map(n => {
            if (!n.className) return n;
            const cleaned = n.className.replace(/\bsubtree-preview-\S+/g, "").trim() || undefined;
            return cleaned !== n.className ? { ...n, className: cleaned } : n;
        }));
    }, [setNodes]);

    // Clear any lingering subtree preview when the selected node changes or toolbar unmounts.
    // Fires on every selectedNodeId change (not just unmount) — this is intentional because
    // a stale preview from the previous node must be removed before the new node's preview.
    React.useEffect(() => {
        return () => clearPreview();
    }, [selectedNodeId, singleNodeSelected, clearPreview]);

    // Build delete label
    const deleteParts: string[] = [];
    if (selection.deletableNodeCount > 0) deleteParts.push(`${selection.deletableNodeCount} node${selection.deletableNodeCount !== 1 ? "s" : ""}`);
    if (selection.selectedEdgeIds.length > 0) deleteParts.push(`${selection.selectedEdgeIds.length} link${selection.selectedEdgeIds.length !== 1 ? "s" : ""}`);
    const deleteLabel = isMulti
        ? `Delete ${deleteParts.join(" and ")}${includesRoot ? " (root excluded)" : ""}`
        : singleNodeSelected ? (selectedNodeIsRoot ? "Delete (root)" : "Delete Node") : "Remove Link";

    return (
        <Panel position="bottom-center">
            <div className="selection-toolbar">
                <SpaceBetween direction="horizontal" size="xs">
                    <span className="selection-toolbar__count">
                        {selection.selectedNodeIds.length > 0 && <span>🔵 {selection.selectedNodeIds.length} node{selection.selectedNodeIds.length !== 1 ? "s" : ""}</span>}
                        {selection.selectedNodeIds.length > 0 && selection.selectedEdgeIds.length > 0 && <span> · </span>}
                        {selection.selectedEdgeIds.length > 0 && <span>🔗 {selection.selectedEdgeIds.length} link{selection.selectedEdgeIds.length !== 1 ? "s" : ""}</span>}
                    </span>
                    {singleNodeSelected && <Button onClick={() => onNodeDoubleClick({} as any, { id: selectedNodeId } as any)} variant="link">✏️ Edit</Button>}
                    {singleEdgeSelected && onEditAnswer && (() => {
                        const edgeData = edges.find(e => e.id === selection.selectedEdgeIds[0]);
                        return edgeData ? <Button onClick={() => setEditingAnswer({ sourceNodeId: edgeData.source, targetNodeId: edgeData.target, currentAnswer: edgeData.data?.optionText ?? "", position: { x: window.innerWidth / 2, y: window.innerHeight / 2 } })} variant="link">✏️ Edit Answer</Button> : null;
                    })()}
                    {singleNodeSelected && onCopyNode && <Button onClick={() => onCopyNode(selectedNodeId)} variant="link">📋 Copy <span style={{ opacity: 0.6, fontSize: "11px" }}>(Ctrl+C)</span></Button>}
                    {singleNodeSelected && onCopySubtree && selectedNodeHasChildren && (
                        <span onMouseEnter={() => showPreview("copy")} onMouseLeave={clearPreview}>
                            <Button onClick={() => { clearPreview(); onCopySubtree(selectedNodeId); }} variant="link">🌳 Copy Node & All Below</Button>
                        </span>
                    )}
                    {hasDeletable && !(selectedNodeIsRoot && !isMulti) && <Button onClick={onDeleteSelected} variant="link">🗑️ {deleteLabel} <span style={{ opacity: 0.6, fontSize: "11px" }}>(Del)</span></Button>}
                    {singleNodeSelected && onDeleteSubtree && selectedNodeHasChildren && !selectedNodeIsRoot && (
                        <span onMouseEnter={() => showPreview("delete")} onMouseLeave={clearPreview}>
                            <Button onClick={() => { clearPreview(); onDeleteSubtree(selectedNodeId); }} variant="link">🗑️ Delete Node & All Below</Button>
                        </span>
                    )}
                    <Button onClick={onClearSelection} variant="link">✖ Clear <span style={{ opacity: 0.6, fontSize: "11px" }}>(Esc)</span></Button>
                </SpaceBetween>
            </div>
        </Panel>
    );
};

/**
 * Node Inspector Panel — fixed "magnifying glass" in the top-left corner.
 * Shows the full overview text of the currently hovered node at a readable size,
 * independent of canvas zoom level. Listens for "node-inspector" CustomEvents
 * dispatched by CustomNode on hover.
 *
 * Why a fixed panel instead of a tooltip?
 * - User's eye stays in one place — no need to chase a moving tooltip
 * - Always visible at readable size, even when zoomed out on large trees
 * - Doesn't overlap or block other nodes
 */
const NodeInspectorPanel: React.FC = () => {
    const [inspectedNode, setInspectedNode] = React.useState<{
        nodeId: string;
        overviewText: string;
        isRoot: boolean;
        isLeaf: boolean;
        isOrphan: boolean;
    } | null>(null);
    // Track active nodeId so we can ignore dismiss events from other nodes.
    const activeNodeId = React.useRef<string | null>(null);

    React.useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (!detail) {
                // Legacy null detail — clear unconditionally
                activeNodeId.current = null;
                setInspectedNode(null);
            } else if ("dismiss" in detail) {
                // Dismiss request — only clear if the dismissed node is the active one.
                // This prevents an unrelated node's unmount or mouseLeave from blanking
                // the panel while a different node is being inspected.
                if (activeNodeId.current === detail.dismiss) {
                    activeNodeId.current = null;
                    setInspectedNode(null);
                }
            } else if (detail.overviewText !== undefined) {
                // New node to inspect
                activeNodeId.current = detail.nodeId;
                setInspectedNode(detail);
            }
        };
        window.addEventListener("node-inspector", handler);
        return () => window.removeEventListener("node-inspector", handler);
    }, []);

    if (!inspectedNode) return null;

    return (
        <div className="node-inspector">
            <div className="node-inspector__header">
                {inspectedNode.isRoot && <span className="node-inspector__badge node-inspector__badge--root">ROOT</span>}
                {inspectedNode.isLeaf && <span className="node-inspector__badge node-inspector__badge--leaf">LEAF</span>}
                {inspectedNode.isOrphan && <span className="node-inspector__badge node-inspector__badge--orphan">UNLINKED</span>}
                <span className="node-inspector__title">🔍 Node Overview</span>
            </div>
            <div className="node-inspector__body">
                {inspectedNode.overviewText}
            </div>
        </div>
    );
};

export const ReactFlowCanvas: React.FC<ReactFlowCanvasProps> = (props) => {
    const [helperExpanded, setHelperExpanded] = React.useState(false);
    // --- Multi-select: selection state ---
    const [selection, setSelection] = React.useState<SelectionState>({
        selectedNodeIds: [],
        selectedEdgeIds: [],
        deletableNodeCount: 0
    });

    const multiSelectEnabled = !!props.enhancementLabEnabled;
    const hasSelection = selection.selectedNodeIds.length > 0 || selection.selectedEdgeIds.length > 0;

    /**
     * Handle node changes from ReactFlow — only propagate "select" changes.
     * Position/dimension changes are handled separately by our drag handlers.
     * Without this, selection state doesn't persist in the controlled nodes array,
     * so Shift+click to multi-select would fail (each click replaces the previous selection).
     */
    const handleNodesChange = React.useCallback((changes: NodeChange[]) => {
        const selectChanges = changes.filter(c => c.type === "select");
        if (selectChanges.length > 0) {
            props.layoutManager.onNodesChange(selectChanges);
        }
    }, [props.layoutManager.onNodesChange]);

    /**
     * Handle edge changes from ReactFlow — only propagate "select" changes.
     * Without this, edge selection doesn't persist, breaking multi-select for edges.
     */
    const handleEdgesChange = React.useCallback((changes: EdgeChange[]) => {
        const selectChanges = changes.filter(c => c.type === "select");
        if (selectChanges.length > 0) {
            props.layoutManager.onEdgesChange(selectChanges);
        }
    }, [props.layoutManager.onEdgesChange]);

    /**
     * Track selection changes from ReactFlow.
     * Updates our selection state with the currently selected nodes/edges.
     * Filters out root node from deletable count since root cannot be deleted.
     */
    const handleSelectionChange = React.useCallback(({ nodes, edges }: OnSelectionChangeParams) => {
        const selectedNodeIds = nodes.map(n => n.id);
        const selectedEdgeIds = edges.map(e => e.id);
        const deletableNodeCount = selectedNodeIds.filter(id => id !== props.rootNodeId).length;
        setSelection({ selectedNodeIds, selectedEdgeIds, deletableNodeCount });
    }, [props.rootNodeId]);

    /** Clear selection programmatically — also deselects nodes/edges in ReactFlow */
    const clearSelection = React.useCallback(() => {
        setSelection({ selectedNodeIds: [], selectedEdgeIds: [], deletableNodeCount: 0 });
        props.layoutManager.setNodes(nds => nds.map(n => n.selected ? { ...n, selected: false } : n));
        props.layoutManager.setEdges(eds => eds.map(e => e.selected ? { ...e, selected: false } : e));
    }, [props.layoutManager.setNodes, props.layoutManager.setEdges]);

    /**
     * Unified handler: delete all selected nodes and/or remove all selected links
     * in a single API call. Collects both node IDs and edge data, then delegates
     * to the parent's onDeleteSelected handler which does smart dedup.
     */
    const handleDeleteSelected = React.useCallback(() => {
        if (!props.onDeleteSelected) return;

        const deletableNodeIds = selection.selectedNodeIds.filter(id => id !== props.rootNodeId);
        const selectedEdges = props.layoutManager.edges
            .filter(e => selection.selectedEdgeIds.includes(e.id))
            .map(e => ({
                sourceNodeId: e.source,
                targetNodeId: e.target,
                answerText: e.data?.optionText ?? ""
            }));

        if (deletableNodeIds.length === 0 && selectedEdges.length === 0) return;

        props.onDeleteSelected({ nodeIds: deletableNodeIds, edges: selectedEdges });
    }, [props.onDeleteSelected, selection.selectedNodeIds, selection.selectedEdgeIds, props.rootNodeId, props.layoutManager.edges]);

    /**
     * Unified keyboard shortcut handler.
     * Handles Delete/Backspace, Escape, Ctrl+C, Ctrl+V in a single listener
     * to avoid duplicate event handling and ordering issues.
     */
    React.useEffect(() => {
        if (!props.enhancementLabEnabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't intercept if user is typing in an input/textarea
            const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
            if (tag === "input" || tag === "textarea" || (e.target as HTMLElement)?.isContentEditable) return;

            // Delete / Backspace — delete selected elements
            if (e.key === "Delete" || e.key === "Backspace") {
                if ((selection.deletableNodeCount > 0 || selection.selectedEdgeIds.length > 0) && props.onDeleteSelected) {
                    e.preventDefault();
                    handleDeleteSelected();
                }
            }

            // Ctrl+C / Cmd+C — copy selected node to clipboard
            // Skip if user has text selected (let native copy work)
            if ((e.ctrlKey || e.metaKey) && e.key === "c") {
                if (window.getSelection()?.toString()) return; // Let native copy handle text selection
                if (selection.selectedNodeIds.length === 1 && selection.selectedEdgeIds.length === 0) {
                    e.preventDefault();
                    if (props.onCopyNode) {
                        props.onCopyNode(selection.selectedNodeIds[0]);
                    }
                }
            }

            // Ctrl+V / Cmd+V — paste at viewport center
            if ((e.ctrlKey || e.metaKey) && e.key === "v") {
                if (props.clipboard && props.onPaste && rfInstance.current) {
                    e.preventDefault();
                    const centerPosition = rfInstance.current.screenToFlowPosition({
                        x: window.innerWidth / 2,
                        y: window.innerHeight / 2
                    });
                    props.onPaste(centerPosition);
                }
            }

            // Escape — clear selection and/or clipboard
            if (e.key === "Escape") {
                if (props.clipboard && props.onClearClipboard) {
                    props.onClearClipboard();
                }
                if (hasSelection) {
                    clearSelection();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [props.enhancementLabEnabled, selection, hasSelection, handleDeleteSelected, clearSelection,
        props.clipboard, props.onPaste, props.onCopyNode, props.onClearClipboard]);

    // --- Drag-to-link: connection state ---
    const [pendingConnection, setPendingConnection] = React.useState<PendingConnection | null>(null);
    // --- Edit answer popover state ---
    const [editingAnswer, setEditingAnswer] = React.useState<{
        sourceNodeId: string;
        targetNodeId: string;
        currentAnswer: string;
        position: { x: number; y: number };
    } | null>(null);
    /** Ref to capture mouse position for popover placement (updated only during connection events) */
    const lastMousePos = React.useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    /** Cleanup function for temporary mousemove listener during connection drag */
    const mouseTrackCleanup = React.useRef<(() => void) | null>(null);

    // Safety net: remove mousemove listener if component unmounts mid-drag
    React.useEffect(() => {
        return () => {
            mouseTrackCleanup.current?.();
            mouseTrackCleanup.current = null;
        };
    }, []);

    /**
     * Cycle detection: checks if adding an edge source→target would create a cycle.
     * Uses iterative DFS from target node following existing children links.
     *
     * Optionally accepts an edge to exclude from the graph (used by relink/reconnection
     * to ignore the old edge being replaced). Without this, relinking would always
     * detect a false positive when the old edge creates a path back to source.
     *
     * Shared by both isValidConnection (drag-to-connect) and handleEdgeUpdate (relink).
     */
    const wouldCreateCycle = React.useCallback((
        sourceId: string,
        targetId: string,
        excludeEdge?: { sourceId: string; targetId: string }
    ): boolean => {
        if (sourceId === targetId) return true; // Self-loop
        if (!props.getContentForValidation) return false;

        const content = props.getContentForValidation();
        const nodeMap = new Map<string, Array<{ nodeId: string }>>();
        for (const node of content.allNodes) {
            if (excludeEdge && node.id === excludeEdge.sourceId) {
                // Exclude the specified edge from this node's children
                nodeMap.set(node.id, node.children.filter(c => c.nodeId !== excludeEdge.targetId));
            } else {
                nodeMap.set(node.id, node.children);
            }
        }

        // Check if there's already a path from target to source (which would form a cycle)
        const visited = new Set<string>();
        const stack = [targetId];
        while (stack.length > 0) {
            const current = stack.pop()!;
            if (current === sourceId) return true; // Found path back to source → cycle!
            if (visited.has(current)) continue;
            visited.add(current);
            const children = nodeMap.get(current) || [];
            for (const child of children) {
                if (!visited.has(child.nodeId)) {
                    stack.push(child.nodeId);
                }
            }
        }
        return false;
    }, [props.getContentForValidation]);

    /**
     * ReactFlow isValidConnection callback — called during drag to validate connection.
     * Blocks: self-loops, cycles, duplicate edges.
     * Controls the visual feedback (green ✓ vs red ✗ connection line).
     */
    const isValidConnection = React.useCallback((connection: Connection): boolean => {
        if (!connection.source || !connection.target) return false;
        if (connection.source === connection.target) return false;

        // Check for duplicate edge
        const existingEdge = props.layoutManager.edges.find(
            e => e.source === connection.source && e.target === connection.target
        );
        if (existingEdge) return false;

        // Check for cycles
        if (wouldCreateCycle(connection.source, connection.target)) return false;

        return true;
    }, [props.layoutManager.edges, wouldCreateCycle]);

    /**
     * Capture mouse position only during active connection drag.
     * Avoids a global mousemove listener that fires on every mouse movement.
     */
    const handleConnectStart = React.useCallback(() => {
        const trackMouse = (e: MouseEvent) => {
            lastMousePos.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener("mousemove", trackMouse);
        mouseTrackCleanup.current = () => {
            window.removeEventListener("mousemove", trackMouse);
        };
    }, []);

    const handleConnectEnd = React.useCallback(() => {
        mouseTrackCleanup.current?.();
        mouseTrackCleanup.current = null;
    }, []);

    /**
     * onConnect: called when user completes a valid connection (drops handle on target).
     * Instead of immediately adding the edge, shows the answer text popover.
     */
    const handleConnect = React.useCallback((connection: Connection) => {
        if (!connection.source || !connection.target) return;
        if (!props.onAddLink) return;

        setPendingConnection({
            sourceNodeId: connection.source,
            targetNodeId: connection.target,
            position: { ...lastMousePos.current }
        });
    }, [props.onAddLink]);

    /** User confirmed the connection with answer text → call parent handler */
    const handleConnectionConfirm = React.useCallback((answerText: string) => {
        if (pendingConnection && props.onAddLink) {
            props.onAddLink(pendingConnection.sourceNodeId, pendingConnection.targetNodeId, answerText);
        }
        setPendingConnection(null);
    }, [pendingConnection, props.onAddLink]);

    /** User cancelled the connection popover */
    const handleConnectionCancel = React.useCallback(() => {
        setPendingConnection(null);
    }, []);

    /**
     * Edge reconnection (relink): called when user drags an existing edge endpoint
     * to a different node. ReactFlow calls onEdgeUpdate with the old edge and the
     * new connection. We validate (no self-loop, no duplicate, no cycle) and then
     * delegate to the parent handler which performs the API call.
     *
     * The answer text from the original edge is preserved — the user is just
     * changing where the link points to, not what it says.
     */
    const handleEdgeUpdate = React.useCallback((oldEdge: Edge, newConnection: Connection) => {
        if (!props.onReconnectEdge) return;
        if (!newConnection.source || !newConnection.target) return;

        const oldSourceId = oldEdge.source;
        const oldTargetId = oldEdge.target;
        const newSourceId = newConnection.source;
        const newTargetId = newConnection.target;

        // No-op if nothing changed
        if (oldSourceId === newSourceId && oldTargetId === newTargetId) return;

        // Validate: no self-loop
        if (newSourceId === newTargetId) {
            console.warn(`[ReconnectEdge] Rejected — self-loop (${newSourceId} → ${newTargetId})`);
            return;
        }

        // Validate: no duplicate edge (check via content model, not controlled edges array)
        if (props.getContentForValidation) {
            const content = props.getContentForValidation();
            const sourceNode = content.allNodes.find(n => n.id === newSourceId);
            if (sourceNode?.children.some(c => c.nodeId === newTargetId)) {
                console.warn(`[ReconnectEdge] Rejected — duplicate edge (${newSourceId} → ${newTargetId} already exists)`);
                return;
            }
        }

        // Validate: no cycle — reuse shared wouldCreateCycle with edge exclusion
        // to ignore the old edge being replaced (otherwise it would always false-positive)
        if (wouldCreateCycle(newSourceId, newTargetId, { sourceId: oldSourceId, targetId: oldTargetId })) {
            console.warn(`[ReconnectEdge] Rejected — would create cycle (${newSourceId} → ${newTargetId})`);
            return;
        }

        // Extract the answer text from the old edge data (typed via CustomEdge.Data)
        const edgeData = oldEdge.data as CustomEdge.Data | undefined;
        const answerText = edgeData?.optionText ?? "";

        // Delegate to parent handler which performs the content-level optimistic update
        // (updates currentContent + layoutManager.update) BEFORE the API call, preventing
        // any snap-back flash. No need for edge-level reconnectEdge() here — the content
        // model update drives the edge rebuild through the layout manager.
        props.onReconnectEdge(oldSourceId, oldTargetId, newSourceId, newTargetId, answerText);
    }, [props.onReconnectEdge, props.getContentForValidation, wouldCreateCycle]);

    // --- Clipboard cursor badge: ref-based to avoid re-renders on every mousemove ---
    const badgeRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!props.clipboard) {
            if (badgeRef.current) badgeRef.current.style.display = "none";
            return;
        }
        const handleMouseMove = (e: MouseEvent) => {
            if (badgeRef.current) {
                badgeRef.current.style.left = `${e.clientX + 16}px`;
                badgeRef.current.style.top = `${e.clientY + 16}px`;
                badgeRef.current.style.display = "block";
            }
        };
        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, [props.clipboard]);

    // Store ReactFlow instance for coordinate conversion (screen → flow coords for paste)
    const rfInstance = React.useRef<ReactFlowInstance | null>(null);

    // Use onInit to fitView after ReactFlow is fully initialized.
    const handleInit = React.useCallback((instance: ReactFlowInstance) => {
        rfInstance.current = instance;
        instance.fitView(FIT_VIEW_OPTIONS);
    }, []);

    /**
     * Paste clipboard content when clicking on empty canvas.
     * Converts screen coordinates to flow coordinates using the ReactFlow instance.
     */
    const handlePaneClick = React.useCallback((event: React.MouseEvent) => {
        if (!props.clipboard || !props.onPaste || !rfInstance.current) return;

        // Convert screen position to flow coordinates
        const flowPosition = rfInstance.current.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY
        });

        props.onPaste(flowPosition);
    }, [props.clipboard, props.onPaste]);

    const getNodeColor = React.useCallback((n: any) => {
        if (n.data?.isOrphan) return ORPHAN_PURPLE; // Purple for orphan nodes
        return n.id === props.rootNodeId ? "#229954" : "#E78C8C";
    }, [props.rootNodeId]);

    const getNodeStrokeColor = React.useCallback((n: any) => {
        if (n.data?.isOrphan) return ORPHAN_PURPLE; // Purple for orphan nodes
        return n.id === props.rootNodeId ? "#229954" : "#E78C8C";
    }, [props.rootNodeId]);

    return (
        <>
        <ReactFlow
            key={props.rfKey}
            onInit={handleInit}
            nodes={props.layoutManager.nodes}
            edges={props.layoutManager.edges}
            onNodeDoubleClick={props.onNodeDoubleClick}
            onNodeDragStart={props.onNodeDragStart}
            onNodeDragStop={props.onNodeDragStop}
            onNodesChange={multiSelectEnabled ? handleNodesChange : undefined}
            onEdgesChange={multiSelectEnabled ? handleEdgesChange : undefined}
            nodesDraggable={props.layoutManager.useCustomizedLayout}
            nodeDragHandle=".node-drag-handle"
            selectNodesOnDrag={false}
            nodesConnectable={props.enhancementLabEnabled && !!props.onAddLink}
            onConnectStart={props.enhancementLabEnabled ? handleConnectStart : undefined}
            onConnectEnd={props.enhancementLabEnabled ? handleConnectEnd : undefined}
            onConnect={props.enhancementLabEnabled ? handleConnect : undefined}
            isValidConnection={props.enhancementLabEnabled ? isValidConnection : undefined}
            connectOnClick={false}
            // --- Edge reconnection (relink): drag edge endpoint to a different node ---
            // Lab-gated: only enabled when enhancement lab is active and handler is provided.
            // edgesUpdatable makes edge endpoints draggable; onEdgeUpdate fires when dropped on a new node.
            edgesUpdatable={props.enhancementLabEnabled && !!props.onReconnectEdge}
            onEdgeUpdate={props.enhancementLabEnabled && props.onReconnectEdge ? handleEdgeUpdate : undefined}
            elementsSelectable={true}
            elevateNodesOnSelect={false}
            zoomOnDoubleClick={false}
            nodeTypes={props.nodeTypes}
            edgeTypes={props.edgeTypes}
            // --- Multi-select: Shift+click individual elements, Shift+drag box select ---
            multiSelectionKeyCode={multiSelectEnabled ? "Shift" : null}
            selectionOnDrag={false}
            onSelectionChange={multiSelectEnabled ? handleSelectionChange : undefined}
            // Prevent ReactFlow's default Delete key handler — we handle it ourselves
            // to show confirmation dialog and exclude root node
            onPaneClick={props.clipboard ? handlePaneClick : undefined}
            deleteKeyCode={null}>
            <Panel position="top-left"><SpaceBetween direction="horizontal" size="s">
                <Button onClick={props.closeEditor} variant="primary">Go Back to Page Draft</Button>
                {props.onCreateOrphanNode && (
                    <Button onClick={props.onCreateOrphanNode} iconName="add-plus">
                        Create Node
                    </Button>
                )}
                <Toggle
                    checked={props.layoutManager.useCustomizedLayout}
                    onChange={(e) => props.handleLayoutToggle(e.detail.checked)}
                >
                    Customized Layout
                </Toggle>
                {(props.orphanNodeCount ?? 0) > 0 && (
                    <span style={{
                        background: ORPHAN_PURPLE,
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "bold",
                        padding: "2px 8px",
                        borderRadius: "10px"
                    }}>
                        {props.orphanNodeCount} unlinked {props.orphanNodeCount === 1 ? "node" : "nodes"}
                    </span>
                )}
                {props.saveStatus === "saving" && (
                    <span style={{ color: "#0972d3", fontSize: "14px" }}>
                        {props.saveType === "layout" ? "Saving layout preference..." :
                         props.saveType === "drag" ? "Saving positions..." :
                         props.saveType === "edit" ? "Updating content..." :
                         "Saving changes..."}
                    </span>
                )}
                {props.saveStatus === "saved" && (
                    <span style={{ color: "#037f0c", fontSize: "14px" }}>
                        ✓ All changes saved
                    </span>
                )}
                {props.saveStatus === "stale" && (
                    <span style={{ color: "#e07800", fontSize: "14px" }}>
                        ✓ Edit saved. Refresh page to see latest changes.
                    </span>
                )}
                {props.saveStatus === "error" && (
                    <span style={{ color: "#d91515", fontSize: "14px" }}>
                        Save failed — please retry
                    </span>
                )}
            </SpaceBetween></Panel>
            {props.showDragTip && (
                <Panel position="top-center">
                    <div style={DRAG_TIP_STYLE}>
                        💡 Tip: You can now drag nodes to reposition them!
                    </div>
                </Panel>
            )}
            <Panel position="top-right">
                <div style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                    maxWidth: helperExpanded ? "360px" : "auto",
                    transition: "max-width 0.2s ease"
                }}>
                    <div
                        onClick={() => setHelperExpanded(!helperExpanded)}
                        style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            userSelect: "none",
                            fontWeight: 600,
                            fontSize: "14px"
                        }}
                    >
                        <span style={{
                            display: "inline-block",
                            transform: helperExpanded ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease"
                        }}>▶</span>
                        Helper
                    </div>
                    {helperExpanded && (
                        <div style={{ padding: "0 12px 12px 12px" }}>
                            <TextContent>
                                <ul>
                                    <li><strong>Double-click node</strong>: Open edit modal</li>
                                    {props.enhancementLabEnabled && <>
                                        <li><strong>Click node/link</strong>: Select it — actions appear in the bottom bar</li>
                                        <li><strong>Drag handle ● to node</strong>: Draw a new connection between nodes</li>
                                        <li><strong>Drag edge endpoint</strong>: Relink — move an existing connection to a different node (answer text is preserved)</li>
                                    </>}
                                    <li><strong>Customized Layout toggle</strong>:
                                        <ul>
                                            <li><strong>ON</strong>: Drag nodes to reposition</li>
                                            <li><strong>OFF</strong>: Auto layout</li>
                                        </ul>
                                    </li>
                                    {props.enhancementLabEnabled && (
                                        <li><strong>Create Node</strong>: Add standalone nodes, connect later</li>
                                    )}
                                    {props.enhancementLabEnabled && <>
                                        <li><strong>Shift + click</strong>: Multi-select nodes and links</li>
                                        <li><strong>Ctrl + C</strong>: Copy selected node to clipboard</li>
                                        <li><strong>Ctrl + V</strong>: Paste at viewport center</li>
                                        <li><strong>Click canvas</strong>: Paste at click position (when clipboard active)</li>
                                        <li><strong>Delete / Backspace</strong>: Delete selected elements</li>
                                        <li><strong>Escape</strong>: Clear selection / clipboard</li>
                                    </>}
                                </ul>
                                <h4>Node Colors</h4>
                                <ul>
                                    <li><span style={{ color: "green" }}>Green</span> — root (start) node</li>
                                    <li><span style={{ color: "red" }}>Red</span> — leaf (ending) nodes</li>
                                    {props.enhancementLabEnabled && (
                                        <li><span style={{ color: ORPHAN_PURPLE }}>Purple</span> — unlinked nodes (not connected to the main flow)</li>
                                    )}
                                </ul>
                                {props.enhancementLabEnabled && <>
                                    <h4>Edge Colors</h4>
                                    <ul>
                                        <li><span style={{ color: "black" }}>Black (solid)</span> — normal tree connections</li>
                                        <li><span style={{ color: ORPHAN_PURPLE }}>Purple (dashed)</span> — within unlinked branches</li>
                                        <li><span style={{ color: CROSS_ORPHAN_BLUE }}>Blue (dashed)</span> — reconnecting an unlinked node</li>
                                        <li><span style={{ color: CROSS_CONNECT_AMBER }}>Amber (dotted)</span> — linking to an unlinked node</li>
                                    </ul>
                                </>}
                            </TextContent>
                        </div>
                    )}
                </div>
            </Panel>
            {/* --- Clipboard indicator: prominent banner when something is copied --- */}
            {props.clipboard && (
                <Panel position="top-center">
                    <div className="clipboard-banner">
                        <span>📋 {props.clipboard.type === "subtree" ? "Subtree" : "Node"} copied — click canvas to paste or Ctrl+V</span>
                        {props.onClearClipboard && (
                            <span
                                className="clipboard-banner__close"
                                onClick={props.onClearClipboard}
                                title="Cancel paste (Esc)"
                            >✖</span>
                        )}
                    </div>
                </Panel>
            )}
            {/* --- Selection action bar --- */}
            {multiSelectEnabled && hasSelection && (
                <SelectionToolbar
                    selection={selection}
                    rootNodeId={props.rootNodeId}
                    edges={props.layoutManager.edges}
                    onNodeDoubleClick={props.onNodeDoubleClick}
                    onEditAnswer={props.onEditAnswer}
                    onCopyNode={props.onCopyNode}
                    onCopySubtree={props.onCopySubtree}
                    onDeleteSelected={handleDeleteSelected}
                    onDeleteSubtree={props.onDeleteSubtree}
                    onClearSelection={clearSelection}
                    setEditingAnswer={setEditingAnswer}
                    setNodes={props.layoutManager.setNodes}
                    getContentForValidation={props.getContentForValidation}
                />
            )}
            <Controls />
            <MiniMap
                zoomable
                pannable
                nodeColor={getNodeColor}
                nodeStrokeColor={getNodeStrokeColor}
            />
            <Background variant={BackgroundVariant.Lines} gap={12} size={1} />
        </ReactFlow>
        {/* Node Inspector: fixed panel in top-left showing hovered node's full overview text.
            Acts as a "magnifying glass" — user can stay zoomed out on a large tree while reading
            full node content in this always-readable panel. Listens for CustomEvent "node-inspector"
            dispatched by CustomNode on hover. */}
        <NodeInspectorPanel />
        {pendingConnection && (
            <ConnectionAnswerPopover
                connection={pendingConnection}
                onConfirm={handleConnectionConfirm}
                onCancel={handleConnectionCancel}
            />
        )}
        {/* --- Clipboard cursor badge: ref-based, no re-renders on mousemove --- */}
        {props.clipboard && (
            <div ref={badgeRef} className="clipboard-badge">
                📋 Paste here
            </div>
        )}
        {editingAnswer && (
            <ConnectionAnswerPopover
                connection={{
                    sourceNodeId: editingAnswer.sourceNodeId,
                    targetNodeId: editingAnswer.targetNodeId,
                    position: editingAnswer.position
                }}
                initialValue={editingAnswer.currentAnswer}
                title="✏️ Edit Answer"
                description="Update the answer/label for this link:"
                confirmLabel="Save"
                onConfirm={(newAnswer) => {
                    if (props.onEditAnswer) {
                        props.onEditAnswer(
                            editingAnswer.sourceNodeId,
                            editingAnswer.targetNodeId,
                            editingAnswer.currentAnswer,
                            newAnswer
                        );
                    }
                    setEditingAnswer(null);
                }}
                onCancel={() => setEditingAnswer(null)}
            />
        )}
        </>
    );
};
